// Ekran 08 drawer'ları — hepsi tek kabuk, dört varyant (referans: openDrawer).
// Referansta hiçbiri boyut sınıfı almıyor → varsayılan 560px = 'md'.

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde } from '../../components/Drawer'
import { tr, sayiyaCevir, trBugun, trIsoyaCevir, trYerelIso } from '../../lib/trFormat'

import { cariler as cariApi, stokKartlari } from '../masterdata/api'
import { filo, type Arac, type Dorse } from './api'
import { BAKIM_DURUMLARI } from './ortak'


// ------------------------------------------------------------------ sefer

/**
 * K35 uygunluk filtresi. Referans listeyi istemcide süzüyordu; burada aynı
 * süzme YAPILIYOR (kullanıcı uygunsuz aracı hiç görmesin) ama karar sunucuda
 * da tekrar uygulanıyor — ekran bir güvenlik sınırı değil.
 */
function uygunAraclar(araclar: Arac[], dorseler: Dorse[], sividir: boolean): Arac[] {
  return araclar.filter((a) => {
    if (a.durum === 'bakimda') return false
    if (!sividir) return true

    const d = a.dorseId ? dorseler.find((x) => x.id === a.dorseId) : undefined
    return Boolean(d && d.uygunluk === 'sivi' && d.muayene.durum !== 'gecmis'
      && d.muayene.durum !== 'veriYok')
  })
}

export function SeferDrawer({
  araclar, dorseler, ekle, kapat,
}: {
  araclar: Arac[]
  dorseler: Dorse[]
  ekle: (govde: Record<string, unknown>) => Promise<unknown>
  kapat: () => void
}) {
  const { data: cariListesi = [] } = useQuery({
    queryKey: ['cariler', 'atik'],
    queryFn: () => cariApi.liste(undefined, 'atik'),
  })
  const { data: kartListesi = [] } = useQuery({
    queryKey: ['stok-kartlari', 'hammadde'],
    queryFn: () => stokKartlari.liste('hammadde'),
  })

  const [cariId, setCariId] = useState('')
  const [stokId, setStokId] = useState('')
  const [aracId, setAracId] = useState('')
  const [saat, setSaat] = useState('17:30')
  const [miktar, setMiktar] = useState('15')
  const [gonderiliyor, setGonderiliyor] = useState(false)

  useEffect(() => {
    if (!cariId && cariListesi.length) setCariId(cariListesi[0].id)
  }, [cariListesi, cariId])
  useEffect(() => {
    if (!stokId && kartListesi.length) setStokId(kartListesi[0].id)
  }, [kartListesi, stokId])

  const kart = kartListesi.find((k) => k.id === stokId)
  // Sıvılık kartın KM'sinden: sunucudaki denetim de aynı ölçütü kullanıyor.
  const sividir = kart?.km !== null && kart?.km !== undefined && kart.km < 15
  const uygun = useMemo(
    () => uygunAraclar(araclar, dorseler, sividir),
    [araclar, dorseler, sividir],
  )

  useEffect(() => {
    if (uygun.length && !uygun.some((a) => a.id === aracId)) setAracId(uygun[0].id)
    if (!uygun.length) setAracId('')
  }, [uygun, aracId])

  async function gonder() {
    if (!aracId || !cariId || !stokId) return
    setGonderiliyor(true)
    try {
      await ekle({
        tarih: trBugun(),
        saat: `${saat}:00`,
        aracId,
        surucuId: uygun.find((a) => a.id === aracId)?.surucuId ?? null,
        cariId,
        stokKartiId: stokId,
        planT: sayiyaCevir(miktar) || 0,
      })
    } finally {
      setGonderiliyor(false)
    }
  }

  return (
    <>
      <DrawerGovde>
        <div className="fgrid">
          <div className="fld">
            <label>Firma</label>
            <select className="in" value={cariId} onChange={(e) => setCariId(e.target.value)}>
              {cariListesi.map((c) => <option key={c.id} value={c.id}>{c.unvan}</option>)}
            </select>
          </div>
          <div className="fld">
            <label>Atık türü</label>
            <select className="in" value={stokId} onChange={(e) => setStokId(e.target.value)}>
              {kartListesi.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.ad} · {k.km !== null && k.km < 15 ? 'Sıvı' : 'Katı'}
                </option>
              ))}
            </select>
          </div>
          <div className="fld">
            <label>Uygun araç</label>
            <select
              className="in"
              value={aracId}
              disabled={!uygun.length}
              onChange={(e) => setAracId(e.target.value)}
            >
              {uygun.length === 0
                ? <option value="">uygun araç yok</option>
                : uygun.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.plaka} · {a.surucuAd ?? 'sürücüsüz'}{a.dorsePlaka ? ` · ${a.dorsePlaka}` : ''}
                    </option>
                  ))}
            </select>
          </div>
          <div className="fld">
            <label>Saat</label>
            <input className="in mono" value={saat} onChange={(e) => setSaat(e.target.value)} />
          </div>
          <div className="fld">
            <label>Plan tonaj (t)</label>
            <input className="in mono" value={miktar} onChange={(e) => setMiktar(e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 9 }}>
          <div className="info-box">
            <div>
              {sividir ? (
                <>
                  <b>Sıvı yük</b> — yalnız muayenesi geçerli <b>tanker</b> dorseli araçlar
                  listelendi ({uygun.length}).
                </>
              ) : (
                <><b>Katı yük</b> — damper/damperli araçlar uygun ({uygun.length}).</>
              )}
              {kart?.hij && ' ⚠ Hijyenizasyon zorunlu (K19).'}
            </div>
          </div>
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={kapat}>Vazgeç</button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={!aracId || gonderiliyor}
          onClick={gonder}
        >
          Programa ekle
        </button>
      </DrawerAltlik>
    </>
  )
}

// ------------------------------------------------------------------ yakıt

export function DolumDrawer({
  araclar, ekle, kapat,
}: {
  araclar: Arac[]
  ekle: (govde: Record<string, unknown>) => Promise<unknown>
  kapat: () => void
}) {
  const [aracId, setAracId] = useState(araclar[0]?.id ?? '')
  // Tesis duvar saati; ISO'dan kesmek kullanıcıya 3 saat geriyi gösteriyordu.
  const [zaman, setZaman] = useState(() => trYerelIso())
  const [litre, setLitre] = useState('380')
  const [tutar, setTutar] = useState('15.960')
  const [sayac, setSayac] = useState('')
  const [istasyon, setIstasyon] = useState('OPET Konya Yolu')
  const [gonderiliyor, setGonderiliyor] = useState(false)

  const secili = araclar.find((a) => a.id === aracId)

  async function gonder() {
    if (!aracId) return
    setGonderiliyor(true)
    try {
      await ekle({
        zaman: trIsoyaCevir(zaman),
        aracId,
        surucuId: secili?.surucuId ?? null,
        litre: sayiyaCevir(litre) || 0,
        tutar: sayiyaCevir(tutar) || null,
        // Sayaç boş bırakılabilir; sunucu aralığı hesaplamaz ama kayıt açılır.
        sayacKm: sayac.trim() === '' ? null : sayiyaCevir(sayac),
        istasyon,
      })
    } finally {
      setGonderiliyor(false)
    }
  }

  return (
    <>
      <DrawerGovde>
        <div className="fgrid">
          <div className="fld">
            <label>Araç</label>
            <select className="in" value={aracId} onChange={(e) => setAracId(e.target.value)}>
              {araclar.map((a) => (
                <option key={a.id} value={a.id}>{a.plaka} · {a.surucuAd ?? '—'}</option>
              ))}
            </select>
          </div>
          <div className="fld">
            <label>Tarih / saat</label>
            <input
              className="in mono" type="datetime-local"
              value={zaman} onChange={(e) => setZaman(e.target.value)}
            />
          </div>
          <div className="fld">
            <label>Litre</label>
            <input className="in mono" value={litre} onChange={(e) => setLitre(e.target.value)} />
          </div>
          <div className="fld">
            <label>Tutar (₺)</label>
            <input className="in mono" value={tutar} onChange={(e) => setTutar(e.target.value)} />
          </div>
          <div className="fld">
            <label>Sayaç (km)</label>
            <input
              className="in mono" placeholder="—"
              value={sayac} onChange={(e) => setSayac(e.target.value)}
            />
            <div className="fld-hint">
              {secili?.sayacKm != null && `son bilinen ${tr(secili.sayacKm, 0)} km`}
            </div>
          </div>
          <div className="fld">
            <label>İstasyon</label>
            <input className="in" value={istasyon} onChange={(e) => setIstasyon(e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 9 }}>
          <div className="info-box">
            <div>
              Kayıt, <b>YK-0001 Motorin (Filo)</b> stok kartına gider hareketi olarak işlenir;
              sapma analizi araç ay ortalamasına göre yapılır.
            </div>
          </div>
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={kapat}>Vazgeç</button>
        <button className="btn btn-pri" type="button" disabled={!aracId || gonderiliyor} onClick={gonder}>
          Kaydet
        </button>
      </DrawerAltlik>
    </>
  )
}

// ------------------------------------------------------------------ bakım

const BAKIM_TURLERI = [
  'Periyodik Bakım', 'Arıza Onarım', 'Lastik', 'Muayene Hazırlık', 'Kaporta / Cam',
]

export function BakimDrawer({
  araclar, dorseler, ekle, kapat,
}: {
  araclar: Arac[]
  dorseler: Dorse[]
  ekle: (govde: Record<string, unknown>, dosyalar: File[]) => Promise<unknown>
  kapat: () => void
}) {
  const secenekler = useMemo(
    () => [
      ...araclar.map((a) => ({ id: a.id, plaka: a.plaka, tip: 'arac' as const })),
      ...dorseler.map((d) => ({ id: d.id, plaka: d.plaka, tip: 'dorse' as const })),
    ],
    [araclar, dorseler],
  )

  const [sahip, setSahip] = useState(secenekler[0]?.id ?? '')
  const [tur, setTur] = useState(BAKIM_TURLERI[0])
  const [aciklama, setAciklama] = useState('')
  const [servis, setServis] = useState('Tesis Atölye')
  const [sayac, setSayac] = useState('')
  const [tutar, setTutar] = useState('0')
  const [durum, setDurum] = useState<string>('tamamlandi')
  const [bekleyen, setBekleyen] = useState<File[]>([])
  const [gonderiliyor, setGonderiliyor] = useState(false)

  useEffect(() => {
    if (!sahip && secenekler.length) setSahip(secenekler[0].id)
  }, [secenekler, sahip])

  const secili = secenekler.find((s) => s.id === sahip)

  async function gonder() {
    if (!secili) return
    setGonderiliyor(true)
    try {
      await ekle({
        tarih: trBugun(),
        sahipTip: secili.tip,
        sahipId: secili.id,
        tur,
        // Açıklama boşsa işlem türü yazılır — referansın davranışı.
        aciklama: aciklama.trim() === '' ? tur : aciklama,
        servis,
        sayacKm: sayac.trim() === '' ? null : sayiyaCevir(sayac),
        tutar: sayiyaCevir(tutar) || 0,
        durum,
      }, bekleyen)
    } finally {
      setGonderiliyor(false)
    }
  }

  return (
    <>
      <DrawerGovde>
        <div className="fgrid">
          <div className="fld">
            <label>Araç / Dorse</label>
            <select className="in" value={sahip} onChange={(e) => setSahip(e.target.value)}>
              {secenekler.map((s) => <option key={s.id} value={s.id}>{s.plaka}</option>)}
            </select>
          </div>
          <div className="fld">
            <label>İşlem türü</label>
            <select className="in" value={tur} onChange={(e) => setTur(e.target.value)}>
              {BAKIM_TURLERI.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="fld" style={{ gridColumn: '1/-1' }}>
            <label>Açıklama</label>
            <input
              className="in" placeholder="Örn. 60.000 km bakımı — yağ + filtreler"
              value={aciklama} onChange={(e) => setAciklama(e.target.value)}
            />
          </div>
          <div className="fld">
            <label>Servis</label>
            <input className="in" value={servis} onChange={(e) => setServis(e.target.value)} />
          </div>
          <div className="fld">
            <label>Sayaç (km)</label>
            <input
              className="in mono" placeholder="—"
              value={sayac} onChange={(e) => setSayac(e.target.value)}
            />
          </div>
          <div className="fld">
            <label>Tutar (₺)</label>
            <input className="in mono" value={tutar} onChange={(e) => setTutar(e.target.value)} />
          </div>
          <div className="fld">
            <label>Durum</label>
            <select className="in" value={durum} onChange={(e) => setDurum(e.target.value)}>
              {BAKIM_DURUMLARI.map(([k, e]) => <option key={k} value={k}>{e}</option>)}
            </select>
            <div className="fld-hint">
              {durum === 'tamamlandi' ? 'Tutar TL/km maliyetine girer (K38)' : 'Maliyete girmez'}
            </div>
          </div>
        </div>

        <div className="insp-sec-t" style={{ margin: '10px 0 5px' }}>Ekler</div>
        <div style={{ padding: '0 0 6px' }}>
          {bekleyen.length === 0
            ? <span className="cell-sub">Henüz dosya yok</span>
            : bekleyen.map((d, i) => (
                <span className="ek" key={`${d.name}-${i}`}>
                  {d.type.startsWith('image/') && (
                    <img src={URL.createObjectURL(d)} alt="" />
                  )}
                  {d.name}
                </span>
              ))}
        </div>
        <label className="up" style={{ display: 'block' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
          </svg>
          Evrak / resim yükle — PDF, JPG, PNG
          <input
            type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }}
            onChange={(e) => setBekleyen([...bekleyen, ...Array.from(e.target.files ?? [])])}
          />
        </label>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={kapat}>Vazgeç</button>
        <button className="btn btn-pri" type="button" disabled={!secili || gonderiliyor} onClick={gonder}>
          Kaydet
        </button>
      </DrawerAltlik>
    </>
  )
}

// ----------------------------------------------------------------- varlık

const VARLIK_TURLERI = [
  'Çekici', 'Kamyon (Damper)', 'Dorse — Tanker', 'Dorse — Damperli', 'Sürücü',
]

/**
 * Varlık ekleme. Referansta bu drawer <b>hiçbir veri yazmıyordu</b> (alanların
 * id'si bile yok, "Kaydet" yalnız toast basıyordu). Burada gerçekten kaydediyor:
 * türe göre araç, dorse ya da sürücü açar.
 */
export function VarlikDrawer({
  tamamlandi, kapat,
}: {
  tamamlandi: (mesaj: string) => void
  kapat: () => void
}) {
  const [tur, setTur] = useState(VARLIK_TURLERI[0])
  const [ad, setAd] = useState('')
  const [ikinci, setIkinci] = useState('')
  const [vade, setVade] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [gonderiliyor, setGonderiliyor] = useState(false)

  const surucuMu = tur === 'Sürücü'
  const dorseMu = tur.startsWith('Dorse')

  async function gonder() {
    if (!ad.trim()) { setHata(surucuMu ? 'Sürücü adı zorunlu.' : 'Plaka zorunlu.'); return }
    setGonderiliyor(true)
    setHata(null)
    try {
      if (surucuMu) {
        await filo.surucuKaydet(null, { ad, tel: ikinci || null, puan: null })
        tamamlandi(`${ad} sürücü olarak eklendi.`)
      } else if (dorseMu) {
        await filo.dorseKaydet(null, {
          plaka: ad,
          tip: tur.replace('Dorse — ', ''),
          hacim: null,
          uygunluk: tur.includes('Tanker') ? 'sivi' : 'kati',
          muayeneTarihi: vade || null,
        })
        tamamlandi(`${ad} dorsesi eklendi — muayene takibi başladı.`)
      } else {
        await filo.aracKaydet(null, {
          plaka: ad, tip: tur, model: ikinci || null, sayacKm: null, durum: 'hazir',
        } as never)
        tamamlandi(`${ad} aracı eklendi — evrak takibi başlatıldı.`)
      }
    } catch (h) {
      setHata(h instanceof Error ? h.message : 'Kaydedilemedi.')
    } finally {
      setGonderiliyor(false)
    }
  }

  return (
    <>
      <DrawerGovde>
        <div className="fgrid">
          <div className="fld">
            <label>Tür</label>
            <select className="in" value={tur} onChange={(e) => setTur(e.target.value)}>
              {VARLIK_TURLERI.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="fld">
            <label>{surucuMu ? 'Ad' : 'Plaka'}</label>
            <input
              className="in" placeholder={surucuMu ? 'Ad Soyad' : '42 KE ····'}
              value={ad} onChange={(e) => setAd(e.target.value)}
            />
          </div>
          <div className="fld">
            <label>{surucuMu ? 'Telefon' : 'Model'}</label>
            <input
              className="in" placeholder="—"
              value={ikinci} onChange={(e) => setIkinci(e.target.value)}
              disabled={dorseMu}
            />
          </div>
          <div className="fld">
            <label>Muayene / SRC vadesi</label>
            <input
              className="in mono" type="date"
              value={vade} onChange={(e) => setVade(e.target.value)}
              disabled={!dorseMu}
            />
            <div className="fld-hint">
              {dorseMu ? 'K35: muayenesi geçmiş dorse programlanamaz' : 'Evraklar panelden eklenir'}
            </div>
          </div>
        </div>
        {hata && (
          <div className="info-box" style={{ marginTop: 9, background: 'var(--red-bg)', borderColor: 'var(--red-bd)' }}>
            <div>{hata}</div>
          </div>
        )}
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={kapat}>Vazgeç</button>
        <button className="btn btn-pri" type="button" disabled={gonderiliyor} onClick={gonder}>
          Kaydet
        </button>
      </DrawerAltlik>
    </>
  )
}
